// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@rpsc/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@rpsc/App/Components/Product/ProductGridTemplate";
import { ProductAvailabilityStyles } from "@rpsc/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@rpsc/Overrides/Components/ProductPrice";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface PdpProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showDivider?: boolean;
    showAddToCartActions?: boolean;
    showVariants?: boolean;
    containerClassName?: string;
    extendedStyles?: PdpProductPriceAndActionsStyle;
}

const mapStateToProps = (state: ApplicationState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
});

type Props = PdpProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface PdpProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productActions?: ProductActionsStyle;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    divider?: InjectableCss;
}

export const pdpProductPriceAndActionsStyle: PdpProductPriceAndActionsStyle = {};

const PdpProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartActions = true,
    showDivider = false,
    showVariants = true,
    productContext,
    containerClassName,
    canSeePrices,
}) => {
    const [styles] = useState(() => mergeWithCss(pdpProductPriceAndActionsStyle, extendedStyles));
    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            {showVariants && (
                <ProductGridContainer.Info>
                    {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                        fields: {},
                    })}
                </ProductGridContainer.Info>
            )}
            {showPrice && (
                <ProductGridContainer.Price>
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor="actions-uom" id="actions-uom-label">
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} extendedStyles={styles.productPrice} />
                    </StyledWrapper>

                    {canSeePrices && showAvailability && (
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            isProductDetailsPage
                        />
                    )}
                </ProductGridContainer.Price>
            )}

            {canSeePrices && (
                <ProductGridContainer.AddToCartActions>
                    {showDivider && !productContext?.product?.isVariantParent && <StyledWrapper {...styles.divider} />}
                    {showAddToCartActions && <ProductActions isPdp extendedStyles={styles.productActions} />}
                </ProductGridContainer.AddToCartActions>
            )}
            {canSeePrices && (
                <ProductGridContainer.AddToList>
                    {showAddToList && (
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                        />
                    )}
                </ProductGridContainer.AddToList>
            )}
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(PdpProductPriceAndActions));
